import { formatMoney, plural } from '@/lib/format'
import type { Supplier, WeddingData } from '@/mock/types'

/**
 * Removes a supplier from the wedding. What others reference stays: the budget
 * keeps the expense and its payments (just unlinked), the schedule keeps its
 * items without the supplier as the one who handles them.
 */
export function removeSupplier(data: WeddingData, supplierId: string): WeddingData {
  return {
    ...data,
    suppliers: data.suppliers.filter((supplier) => supplier.id !== supplierId),
    budget: {
      ...data.budget,
      expenses: data.budget.expenses.map((expense) =>
        expense.supplierId === supplierId ? { ...expense, supplierId: undefined } : expense,
      ),
    },
    schedule: data.schedule.map((event) =>
      event.owner?.type === 'supplier' && event.owner.id === supplierId
        ? { ...event, owner: undefined }
        : event,
    ),
    scheduleShares: data.scheduleShares.filter((share) => share.supplierId !== supplierId),
  }
}

/** What removing the supplier changes elsewhere, in the confirm dialog; and the gentler option. */
export function RemoveConsequences({ supplier, data }: { supplier: Supplier; data: WeddingData }) {
  const expense = data.budget.expenses.find((item) => item.supplierId === supplier.id)
  const events = data.schedule.filter(
    (event) => !event.removed && event.owner?.type === 'supplier' && event.owner.id === supplier.id,
  ).length
  const effects = [
    expense &&
      `Výdaj v rozpočtu${expense.amount > 0 ? ` (${formatMoney(expense.amount)})` : ''} i jeho platby zůstanou, jen už nebudou propojené s dodavatelem.`,
    events > 0 &&
      `${events} ${plural(events, 'položka', 'položky', 'položek')} v harmonogramu ${plural(events, 'zůstane', 'zůstanou', 'zůstane')} bez toho, kdo ${plural(events, 'ji', 'je', 'je')} zajišťuje.`,
  ].filter(Boolean) as string[]
  return (
    <span className="block space-y-3">
      <span className="block">Zmizí ze seznamu i s kontakty a poznámkami.</span>
      {effects.length > 0 ? (
        <span className="block space-y-1.5">
          {effects.map((effect) => (
            <span key={effect} className="block">
              {effect}
            </span>
          ))}
        </span>
      ) : null}
      {supplier.status !== 'cancelled' ? (
        <span className="block text-muted-foreground">
          Jen spolupráce skončila? Použijte raději „Ukončit spolupráci“, dodavatel vám zůstane v
          přehledu.
        </span>
      ) : null}
    </span>
  )
}
